import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useVetOnboarding } from "@/hooks/useVetOnboarding";
import { recordAuthEvent, recordSignOutReason, logSignOut } from "@/lib/diagnostics";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  onboardingLoading: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: (trigger?: string, reason?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { onboardingLoading } = useVetOnboarding(user);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      recordAuthEvent("INITIAL_SESSION", session);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      recordAuthEvent(event, session);
      if (event === "SIGNED_OUT") {
        // Drop the cached role so a different signed-in user doesn't inherit it.
        import("@/hooks/useUserRole").then((m) => m.clearUserRoleCache()).catch(() => {});
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata?: Record<string, string>) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
        ...(metadata ? { data: metadata } : {}),
      },
    });
    return { error: error as Error | null };
  };


  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async (trigger = "AuthContext.signOut", reason = "User-initiated sign-out") => {
    recordSignOutReason(`${trigger} called: ${reason}`);
    logSignOut({
      userId: user?.id ?? null,
      trigger,
      reason,
      scope: "global",
      stack: new Error().stack,
    });
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, onboardingLoading, signUp, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
